import React, { useState } from "react";
import { Table as AntTable, Input, Space, Button } from "antd";
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { TableProps as AntTableProps, ColumnType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table";

export interface DataTableColumn<T> {
  title: string;
  dataIndex: keyof T | string;
  key?: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  sorter?: boolean | ((a: T, b: T) => number);
  width?: number | string;
  fixed?: "left" | "right";
  align?: "left" | "right" | "center";
}

export interface ActionConfig<T> {
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  editText?: string;
  deleteText?: string;
  customActions?: (record: T) => React.ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  rowKey?: string | ((record: T) => string);
  loading?: boolean;
  actions?: ActionConfig<T>;
  pageSize?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  className?: string;
  bordered?: boolean;
  size?: "small" | "middle" | "large";
  showSizeChanger?: boolean;
  pageSizeOptions?: number[];
}

const DataTableComponent = <T extends Record<string, any>>({
  columns,
  data,
  rowKey = "id",
  loading = false,
  actions,
  pageSize = 10,
  searchable = true,
  searchPlaceholder = "Search...",
  className,
  bordered = true,
  size = "middle",
  showSizeChanger = true,
  pageSizeOptions = [10, 20, 50, 100],
}: DataTableProps<T>): JSX.Element => {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);

  // Filter data based on search text
  const filteredData = React.useMemo(() => {
    if (!searchText) return data;

    return data.filter((record) => {
      return Object.values(record).some((value) => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchText.toLowerCase());
      });
    });
  }, [data, searchText]);

  // Build Ant Design columns
  const antColumns = React.useMemo(() => {
    const cols: ColumnType<T>[] = columns.map((col) => ({
      title: col.title,
      dataIndex: col.dataIndex as string,
      key: col.key || (col.dataIndex as string),
      render: col.render,
      sorter: col.sorter
        ? typeof col.sorter === "function"
          ? col.sorter
          : (a: T, b: T) => {
              const aVal = a[col.dataIndex as keyof T];
              const bVal = b[col.dataIndex as keyof T];
              if (typeof aVal === "string" && typeof bVal === "string") {
                return aVal.localeCompare(bVal);
              }
              if (typeof aVal === "number" && typeof bVal === "number") {
                return aVal - bVal;
              }
              return 0;
            }
        : undefined,
      width: col.width,
      fixed: col.fixed,
      align: col.align,
    }));

    // Add actions column if actions are provided
    if (
      actions &&
      (actions.onEdit || actions.onDelete || actions.customActions)
    ) {
      cols.push({
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 150,
        render: (_: any, record: T) => (
          <Space size="small">
            {actions.customActions ? (
              actions.customActions(record)
            ) : (
              <>
                {actions.onEdit && (
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => actions.onEdit!(record)}
                    size="small"
                  >
                    {actions.editText || "Edit"}
                  </Button>
                )}
                {actions.onDelete && (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => actions.onDelete!(record)}
                    size="small"
                  >
                    {actions.deleteText || "Delete"}
                  </Button>
                )}
              </>
            )}
          </Space>
        ),
      });
    }

    return cols;
  }, [columns, actions]);

  // Pagination configuration
  const paginationConfig: TablePaginationConfig = {
    current: currentPage,
    pageSize: currentPageSize,
    total: filteredData.length,
    showSizeChanger,
    pageSizeOptions,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    onChange: (page, pageSize) => {
      setCurrentPage(page);
      setCurrentPageSize(pageSize);
    },
  };

  return (
    <div className={className}>
      {searchable && (
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder={searchPlaceholder}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            allowClear
            style={{ maxWidth: 400 }}
          />
        </div>
      )}
      <AntTable<T>
        columns={antColumns}
        dataSource={filteredData}
        rowKey={rowKey}
        loading={loading}
        pagination={paginationConfig}
        bordered={bordered}
        size={size}
        scroll={{ x: "max-content", y: 500 }}
      />
    </div>
  );
};

export const AdminTable = React.memo(DataTableComponent) as typeof DataTableComponent;
